<template>
<div class="v-drawer">
  
  <Transition name="fade"
    @before-enter="onEnter">
    <div class="v-drawer__underlay"
      v-if="modelValue"
      @click="onClose">
    </div>
  </Transition>

  <Transition :name="`slide-${side}`">

    <div class="v-drawer__content"
      v-if="modelValue"
      :style="styleContent">
      
      <div class="v-drawer__header"
        ref="header">
        <div class="v-drawer__header-right">
          <div class="v-drawer__title"
            ref="title"
            v-if="title">
            {{ title }}
          </div>
          <div class="v-drawer__subtitle"
            v-if="subtitle">
            {{ subtitle }}
          </div>
        </div>
        <v-icon class="v-drawer__icon-close"
          @click="onClose">
          close
        </v-icon>
      </div>

      <div class="v-drawer__slot"
        v-loading.large="isLoading">
        <slot/>
      </div>

      <div>
        <div class="v-drawer__buttons">
          <button class="v-drawer__button"
            :class="{ 'button-primary': !i, 'button-secondary': i, wide: !!action.label }"
            v-for="(action, i) in actions.filter(e => e.visible ?? true)"
            :disabled="action.disabled"
            :style="action.style"
            @click="action.action">
            <span class="spinner"
              v-if="isSubmitting">
            </span>
            <v-icon v-if="action.icon"
              size="1.5em">
              {{ action.icon }}
            </v-icon>
            <span v-if="action.label">
              {{ action.label }}
            </span>
          </button>
        </div>
        <div class="text-error"
          v-if="hasError">
          {{ $ct('v_drawer_vertical.fix_all_errors') }}
        </div>
      </div>

    </div>

  </Transition>

</div>
</template>

<script>
export default {
  emits: [
    'update:modelValue',
    'enter',
    'close',
  ],
  props: {
    adjustFont: { type: Boolean, required: false, default: false },
    modelValue: { type: Boolean, required: false, default: false },
    hasError: { type: Boolean, required: false, default: false },
    isLoading: { type: Boolean, required: false, default: false },
    title: { type: String, required: false, default: undefined },
    subtitle: { type: String, required: false, default: undefined },
    width: { type: String, required: false, default: '100%' },
    side: { type: String, required: false, default: 'right' },
    duration: { type: String, required: false, default: '150ms' },
    action: { type: Function, required: false, default: undefined },
    actions: { type: Array, required: false, default: [] },
  },
  unmounted() {
    document.body.style.overflowY = 'auto'
  },
  data() {
    return {
      isSubmitting: false,
      maxFontSize: 24,
      minFontSize: 8,
    }
  },
  computed: {
    styleContent() {
      return {
        [this.side]: '4px',
      }
    },
  },
  methods: {
    async onEnter() {
      if (this.action) await this.action()
      this.$nextTick(_ => this.adjustFontSize())
      this.$emit('enter')
    },
    onClose() {
      this.$emit('update:modelValue', false)
      this.$emit('close')
    },
    async submit() {
      this.isSubmitting = true
      await this.actions[0].action()
      this.isSubmitting = false
    },
    adjustFontSize() {
      if (!this.adjustFont) {
        this.fontSize = this.maxFontSize
        return
      }
      const container = this.$refs.header
      const textEl = this.$refs.title
      if (!container || !textEl) return
      let fontSize = this.maxFontSize
      while (fontSize >= this.minFontSize) {
        textEl.style.fontSize = fontSize + 'px'
        const { scrollWidth } = textEl
        const { clientWidth } = container
        if (scrollWidth <= clientWidth * .85) break
        fontSize -= 1
      }
      this.fontSize = fontSize
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        document.body.style.overflowY = val ? 'hidden' : 'auto'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v-drawer {
  position: absolute;
  z-index: 999;
  &__underlay {
    content: '';
    position: fixed;
    top: 0; left: 0;
    right: 0; bottom: 0;
    background-color: #0002;
  }
  &__content {
    position: fixed;
    display: flex;
    flex-direction: column;
    gap: 1.5em;
    top: 4px;
    padding: 2em min(5vw, 2em);
    width: min(v-bind(width), calc(100% - 8px));
    height: calc(100% - 8px);
    background: #fff;
    border-radius: .7em;
  }
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    line-height: 1.35;
  }
  &__header-right {
    max-width: 85%;
    overflow: hidden;
  }
  &__title {
    font-size: calc(v-bind(fontSize) * 1px);
    font-weight: bold;
  }
  &__subtitle {
    margin-top: .35em;
    color: $cdarkgray;
    line-height: 1.5;
  }
  &__icon-close {
    cursor: pointer;
    width: 1em;
    height: 1em;
    line-height: 1em;
    font-size: 2.25em !important;
    border-radius: 7px;
    background: #333;
    text-align: center;
    color: #fff;
  }
  &__slot {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 2em;
    overflow-x: hidden;
    overflow-y: auto;
  }
  &__buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: stretch;
    gap: 1em;
    width: 100%;
    background-color: #fff;
  }
  &__button {
    &.wide { flex-grow: 1; }
    span::first-letter { text-transform: uppercase; }
  }
}

.slide-right-enter-from,
.slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 150ms ease-out;
  opacity: 0;
}
.slide-right-enter-to,
.slide-right-leave-from {
  transform: translateX(0);
  opacity: 1;
}

.slide-left-enter-from,
.slide-left-leave-to {
  transform: translateX(-100%);
}
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 150ms ease-out;
}
.slide-left-enter-to,
.slide-left-leave-from {
  transform: translateX(0);
}
</style>
